/* eslint-disable react/prop-types */
import React, { useState, useEffect, useCallback } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import moment from "moment";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const StatisticChart = ({ queryResult, queryParams }) => {
  const [chartType, setChartType] = useState("month");
  const [chartData, setChartData] = useState(null);

  // 自定義刻度轉換函數
  const customScale = (value) => {
    if (value === 0) return 0;
    return Math.pow(value, 1 / 3); // 使用立方根來壓縮大數值
  };

  const reverseScale = (value) => {
    if (value === 0) return 0;
    return Math.pow(value, 3); // 反向轉換回原始值
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const originalData =
              context.dataset.originalData[context.dataIndex];
            return `$ ${originalData.toLocaleString()}`;
          },
        },
      },
    },
    scales: {
      y: {
        type: "linear",
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            const originalValue = reverseScale(value);
            return `$ ${Math.round(originalValue).toLocaleString()}`;
          },
        },
        grid: {
          color: "rgba(0, 0, 0, 0.1)",
        },
      },
      x: {
        ticks: {
          font: {
            size: 14,
          },
        },
        grid: {
          display: false,
        },
      },
    },
  };

  // Process data by month
  const processMonthlyData = useCallback(() => {
    if (!queryResult?.detailList?.length) return null;

    let monthlyData = {};

    if (queryParams.isUnlimitedTime === "true") {
      // 不限時間的情況，從查詢到的紀錄中統計
      queryResult.detailList.forEach((item) => {
        const monthKey = moment(item.date).format("YYYY-MM");
        monthlyData[monthKey] = (monthlyData[monthKey] || 0) + item.totalAmount;
      });
    } else {
      // 有限時間的情況
      const startDate = moment(
        `${queryParams.startYear}-${queryParams.startMonth}-01`
      );
      const endDate = moment(
        `${queryParams.endYear}-${queryParams.endMonth}-01`
      );

      let currentDate = startDate.clone();
      while (currentDate <= endDate) {
        monthlyData[currentDate.format("YYYY-MM")] = 0;
        currentDate.add(1, "month");
      }

      queryResult.detailList.forEach((item) => {
        const monthKey = moment(item.date).format("YYYY-MM");
        if (Object.prototype.hasOwnProperty.call(monthlyData, monthKey)) {
          monthlyData[monthKey] += item.totalAmount;
        }
      });
    }

    // 將月份按照時間順序排序
    const sortedMonths = Object.keys(monthlyData).sort((a, b) => {
      const [yearA, monthA] = a.split("-").map(Number);
      const [yearB, monthB] = b.split("-").map(Number);
      if (yearA !== yearB) return yearA - yearB;
      return monthA - monthB;
    });

    const originalData = sortedMonths.map((month) => monthlyData[month]);
    const scaledData = originalData.map((value) => customScale(value));

    return {
      labels: sortedMonths.map((month) => {
        const [year, monthNum] = month.split("-");
        return `${year} 年 ${monthNum} 月`;
      }),
      datasets: [
        {
          data: scaledData,
          originalData: originalData,
          backgroundColor: "rgba(75, 192, 192, 0.5)",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
        },
      ],
    };
  }, [queryResult, queryParams]);

  // Process data by construction work
  const processConstructionData = useCallback(() => {
    if (!queryResult?.detailList?.length || queryParams.constructionWorkID)
      return null;

    const constructionData = {};
    queryResult.detailList.forEach((item) => {
      if (
        item.type === "otherExpenditure" &&
        item.allocateConstructionWorkList
      ) {
        // 處理其他支出類型，根據分配列表計算
        item.allocateConstructionWorkList.forEach((allocation) => {
          const key = allocation.constructionWorkName || "未分類";
          constructionData[key] =
            (constructionData[key] || 0) + allocation.allocateAmount;
        });
      } else {
        // 處理一般支出類型
        const key = item.constructionWorkName || "未分類";
        constructionData[key] = (constructionData[key] || 0) + item.totalAmount;
      }
    });

    const sortedKeys = Object.keys(constructionData).sort();
    const originalData = sortedKeys.map((key) => constructionData[key]);
    const scaledData = originalData.map((value) => customScale(value));

    return {
      labels: sortedKeys,
      datasets: [
        {
          data: scaledData,
          originalData: originalData,
          backgroundColor: "rgba(153, 102, 255, 0.5)",
          borderColor: "rgba(153, 102, 255, 1)",
          borderWidth: 1,
        },
      ],
    };
  }, [queryResult, queryParams]);

  // Process data by supplier
  const processSupplierData = useCallback(() => {
    if (!queryResult?.detailList?.length || queryParams.supplierID) return null;

    const supplierData = {};
    queryResult.detailList.forEach((item) => {
      if (item.supplierID) {
        const key = item.payeeName || "未分類";
        supplierData[key] = (supplierData[key] || 0) + item.totalAmount;
      }
    });

    if (Object.keys(supplierData).length === 0) return null;

    const sortedKeys = Object.keys(supplierData).sort();
    const originalData = sortedKeys.map((key) => supplierData[key]);
    const scaledData = originalData.map((value) => customScale(value));

    return {
      labels: sortedKeys,
      datasets: [
        {
          data: scaledData,
          originalData: originalData,
          backgroundColor: "rgba(255, 159, 64, 0.5)",
          borderColor: "rgba(255, 159, 64, 1)",
          borderWidth: 1,
        },
      ],
    };
  }, [queryResult, queryParams]);

  // Update chart data when type or query result changes
  useEffect(() => {
    if (!queryResult) return;

    let newChartData;
    switch (chartType) {
      case "construction":
        newChartData = processConstructionData();
        break;
      case "supplier":
        newChartData = processSupplierData();
        break;
      case "month":
      default:
        newChartData = processMonthlyData();
        break;
    }
    setChartData(newChartData);
  }, [
    chartType,
    queryResult,
    queryParams,
    processMonthlyData,
    processConstructionData,
    processSupplierData,
  ]);

  const canShowConstructionChart = !queryParams?.constructionWorkID;
  const canShowSupplierChart = !queryParams?.supplierID;

  return (
    <div className="mx-10 mt-5 p-10">
      <div className="flex justify-center items-center mb-5">
        <div className="text-xl mr-4">圖表分類方式</div>
        <select
          className="select select-bordered"
          value={chartType}
          onChange={(e) => setChartType(e.target.value)}
        >
          <option value="month">按照月份統計</option>
          {canShowConstructionChart && (
            <option value="construction">按照工程統計</option>
          )}
          {canShowSupplierChart && (
            <option value="supplier">按照廠商統計</option>
          )}
        </select>
      </div>
      <div
        style={{
          width: "100%",
          maxWidth: "1600px",
          margin: "0 auto",
          height: "400px",
          position: "relative",
        }}
      >
        {chartData && (
          <Bar
            options={options}
            data={chartData}
            style={{ width: "100%", height: "100%" }}
          />
        )}
      </div>
    </div>
  );
};

export default StatisticChart;
